import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { notification } from 'antd';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { SendOutlined } from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import MagnifyingGlass from '../../assets/dashboard/MagnifyingGlass';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import QuestionCircle from '../../assets/dashboard/QuestionCircle';
import LetterLogo from '../../assets/dashboard/LetterLogo';
import TrashIcon from '../../assets/dashboard/TrashIcon';
import CopyCheck from '../../assets/search/Check';
import CopyLink from '../../assets/search/Link';
import { t } from '../../i18n/i18n';
import './ShareDashboard.scss';
import { isValidEmail } from '../../utils/emailsUtils';
import DebouncedButton from '../Button/Button';
import CalendarDatePicker from '../CalendarDatePicker/CalendarDatePicker';
import GenericToggle from '../GenericToggle/GenericToggle';
import {
  createDashboardInvitation,
  getDashboardInvitationsByRecruitSessionId,
  softDeleteDashboardInvitation,
} from '../../API/services/dashboardInvitationService';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import Spinner from '../spinner/Spinner';
import {
  Auth0Error,
  DashboardInvitationStatus,
  DashboardAccessLevel,
  NPSProcessTypes,
} from '../../constants/enums';
import { setNPSProcessType } from '../../redux/actions/npsActions';
import { PUBLIC_DASHBOARD_BASE_URL } from '../../constants/routes';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ShareDashboard = ({ selectedSearch }) => {
  const [guestEmail, setGuestEmail] = useState('');
  const [guests, setGuests] = useState([]);
  const [invitationDTO, setInvitationDTO] = useState({
    recruitSessionId: undefined,
    recruitSessionStageId: undefined,
    workspaceId: undefined,
    companyId: undefined,
  });
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingResend, setLoadingResend] = useState({});
  const [copySuccess, setCopySuccess] = useState({});
  const [hasLimitDate, setHasLimitDate] = useState(null);
  const [limitDate, setLimitDate] = useState(null);
  const defaultLimitDate = {
    startDate: { day: null, hours: '23:59' },
    endDate: { day: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), hours: '23:59' },
  };
  const [limitDateData, setLimitDateData] = useState(defaultLimitDate);
  const [isDetailedAccess, setIsDetailedAccess] = useState(true);
  const dispatch = useDispatch();

  const handleGetDashboardInvitation = async () => {
    try {
      const response = await getDashboardInvitationsByRecruitSessionId(selectedSearch.id);
      if (response.status === 200) setGuests(response.data);
    } catch (error) {
      notification.error({
        message: t('SHARE_DASHBOARD_ERROR_FETCH_INVITATIONS'),
        type: 'error',
      });
    }
  };

  const handleSendDashboardInvitation = async () => {
    const accessType = isDetailedAccess
      ? DashboardAccessLevel.DETAIL_ACCESS
      : DashboardAccessLevel.LIMITED_ACCESS;
    const dto = {
      ...invitationDTO,
      guestEmail,
      limitDate: hasLimitDate ? limitDate : null,
      accessType,
    };
    setLoadingSend(true);
    try {
      await createDashboardInvitation(dto);
      await handleGetDashboardInvitation();
      setGuestEmail('');
      setHasLimitDate(false);
      setLimitDateData(defaultLimitDate);
      setIsDetailedAccess(true);
      notification.success({
        message: t('SHARE_DASHBOARD_SUCCESS_INVITATION_SENT'),
        type: 'success',
      });
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const backendStatus = error?.response?.data?.statusCode;
      if (typeof backendMessage === 'string' && backendMessage?.includes(Auth0Error.USER_NOT_OTP)) {
        notification.warning({
          message: t('SHARE_DASHBOARD_SENT_FAILED_USER_EXIST_TITLE'),
          description: t('SHARE_DASHBOARD_SENT_FAILED_USER_EXIST_DESCRIPTION'),
          placement: 'topRight',
          duration: 10,
        });
      } else if (backendStatus === 409) {
        notification.warning({
          message: t('SHARE_DASHBOARD_INVITATION_EXIST_TITLE'),
          description: t('SHARE_DASHBOARD_INVITATION_EXIST_DESCRIPTION'),
          placement: 'topRight',
          duration: 10,
        });
      } else {
        notification.error({
          message: t('SHARE_DASHBOARD_ERROR_INVITATION_SENT'),
          type: 'error',
        });
      }
    } finally {
      setLoadingSend(false);
      dispatch(setNPSProcessType(NPSProcessTypes.EVALUATION_SHARED));
    }
  };

  const handleDeleteGuest = async (guestEmailToDelete) => {
    setLoadingDelete(true);
    try {
      const response = await softDeleteDashboardInvitation(
        invitationDTO.recruitSessionId,
        guestEmailToDelete
      );
      if (response.status === 200) {
        setGuests((prevGuests) =>
          prevGuests.filter(
            (g) => g?.guest?.email !== guestEmailToDelete && g?.guestEmail !== guestEmailToDelete
          )
        );

        notification.success({
          message: t('SHARE_DASHBOARD_SUCCESS_INVITATION_DELETED'),
          type: 'success',
        });
      }
    } catch (error) {
      notification.error({
        message: t('SHARE_DASHBOARD_ERROR_INVITATION_DELETED'),
        type: 'error',
      });
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleResendInvitation = async (guestEmail) => {
    setLoadingResend((prev) => ({ ...prev, [guestEmail]: true }));
    try {
      const accessType = isDetailedAccess
        ? DashboardAccessLevel.DETAIL_ACCESS
        : DashboardAccessLevel.LIMITED_ACCESS;
      const dto = {
        ...invitationDTO,
        guestEmail,
        limitDate: hasLimitDate ? limitDate : null,
        accessType,
      };
      await createDashboardInvitation(dto);
      notification.success({
        message: t('SHARE_DASHBOARD_SUCCESS_INVITATION_RESENT') || 'Invitation resent successfully',
        type: 'success',
      });
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const backendStatus = error?.response?.data?.statusCode;
      if (backendMessage?.includes(Auth0Error.USER_NOT_OTP)) {
        notification.warning({
          message: t('SHARE_DASHBOARD_SENT_FAILED_USER_EXIST_TITLE'),
          description: t('SHARE_DASHBOARD_SENT_FAILED_USER_EXIST_DESCRIPTION'),
          placement: 'topRight',
          duration: 10,
        });
      } else if (backendStatus === 409) {
        notification.warning({
          message: t('SHARE_DASHBOARD_INVITATION_EXIST_TITLE'),
          description: t('SHARE_DASHBOARD_INVITATION_EXIST_DESCRIPTION'),
          placement: 'topRight',
          duration: 10,
        });
      } else {
        notification.error({
          message: t('SHARE_DASHBOARD_ERROR_INVITATION_SENT'),
          type: 'error',
        });
      }
    } finally {
      setLoadingResend((prev) => ({ ...prev, [guestEmail]: false }));
    }
  };

  const handleCopyInvitationLink = async (item) => {
    const guestEmail = item?.guest?.email || item?.guestEmail;
    try {
      // Construct the invitation link
      const invitationLink = `${window.location.origin}${PUBLIC_DASHBOARD_BASE_URL}/${invitationDTO.recruitSessionId}?workspaceId=${invitationDTO.workspaceId}`;
      await navigator.clipboard.writeText(invitationLink);
      setCopySuccess((prev) => ({ ...prev, [guestEmail]: true }));
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [guestEmail]: false }));
      }, 2000);
    } catch (error) {
      notification.error({
        message: t('COPY_LINK_ERROR') || 'Failed to copy link',
        type: 'error',
      });
    }
  };

  const handleGetInvitationItem = (item) => {
    let text;
    let color;

    switch (item?.status) {
      case DashboardInvitationStatus.SENT: {
        text = t('SHARE_DASHBOARD_MODAL_GUESTS_AWAITING_APPROVAL');
        color = 'text-primary';
        break;
      }
      case DashboardInvitationStatus.EXPIRED: {
        text = t('SHARE_DASHBOARD_MODAL_GUESTS_APPLICATION_EXPIRED');
        color = 'text-danger';
        break;
      }
      case DashboardInvitationStatus.ACCEPTED: {
        text = `${t('SHARE_DASHBOARD_MODAL_GUESTS_LAST_CONNECTION')} ${
          item.updatedAt
            ? format(new Date(item.updatedAt), 'dd/MM/yyyy', { locale: es })
            : t('NEVER')
        }`;
        color = item.updatedAt ? 'text-success' : 'text-danger';
        break;
      }
      default: {
        text = t('NEVER');
        color = 'text-muted';
      }
    }

    return <span className={color}>( {text} )</span>;
  };

  useEffect(() => {
    setInvitationDTO({
      recruitSessionId: selectedSearch.id,
      recruitSessionStageId: selectedSearch.stages[0].id,
      workspaceId: selectedSearch.workspaceId,
      companyId: selectedSearch.stages[0].companyId,
    });
    handleGetDashboardInvitation();
  }, []);

  return (
    <div className="d-flex flex-column my-2 gap-1 text-xl font-weight-600">
      <span>{t('SHARE_DASHBOARD_MODAL_TITLE')}</span>
      <div className="d-flex align-items-center gap-px mb-3 text-sm">
        <div className="position-relative w-100">
          <input
            type="text"
            className={`form-control-share-dashboard w-100 ${
              !isValidEmail(guestEmail) &&
              guestEmail.length &&
              'form-control-share-dashboard-border'
            }`}
            placeholder={t('SHARE_DASHBOARD_MODAL_INPUT_PLACEHOLDER')}
            onChange={(e) => setGuestEmail(e.target.value)}
            value={guestEmail}
          />
          <span className="search-icon-share-dashboard">
            <MagnifyingGlass width={20} height={20} color="#757575" />
          </span>
        </div>
        <InfoTooltip
          placement="top"
          icon={<QuestionCircle fill="#757575" />}
          content={<span className="text-xs">{t('SHARE_DASHBOARD_MODAL_TOOLTIP')}</span>}
        />
      </div>

      <div className="d-flex align-items-center gap-2 my-2 text-sm">
        <div className="d-flex align-items-center justify-content-between w-75 ml-5">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm">{t('SHARE_DASHBOARD_DETAILED_ACCESS_LABEL')}</span>
            <InfoTooltip
              placement="top"
              icon={<QuestionCircle fill="#757575" />}
              content={
                <div className="text-xs">
                  <div className="mb-1">{t('SHARE_DASHBOARD_ACCESS_LEVEL_DETAIL_DESCRIPTION')}</div>
                </div>
              }
            />
          </div>
          <GenericToggle
            data-testid="detailed-access-toggle"
            defaultActive={isDetailedAccess}
            actionFunction={() => setIsDetailedAccess(!isDetailedAccess)}
          />
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 my-2">
        <div className="d-flex align-items-center justify-content-between w-75 ml-5">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm">{t('SHARE_DASHBOARD_LIMIT_DATE_LABEL')}</span>
            {hasLimitDate && (
              <CalendarDatePicker
                dateType="endDate"
                stageData={limitDateData}
                setStageData={(data) => {
                  setLimitDateData(data);
                  if (data?.endDate?.day) {
                    setLimitDate(data.endDate.day);
                  }
                }}
                buttonClassNames="border rounded"
                showIconAlways
                restrictTomorrowOnwards
              />
            )}
          </div>
          <GenericToggle
            data-testid="toggle"
            defaultActive={hasLimitDate}
            actionFunction={() => setHasLimitDate(!hasLimitDate)}
          />
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-center">
        <DebouncedButton
          type="primary"
          className="d-flex align-items-center justify-content-center px-3 py-2 text-xs font-weight-600 rounded w-25 mt-3"
          onClick={() => handleSendDashboardInvitation()}
          disabled={!isValidEmail(guestEmail) || !guestEmail.length || loadingSend}
        >
          {loadingSend ? (
            <div className="d-flex align-items-center justify-content-center h-100 w-100">
              <HorizontalLoader white className="h-100 w-100 horizontal-loader-center" />
            </div>
          ) : (
            <span className="d-flex align-items-center gap-1 text-white">
              {t('SHARE_DASHBOARD_BUTTON_INVITE')}
            </span>
          )}
        </DebouncedButton>
      </div>

      <div className="border-bottom w-100 my-4 border-custom-share-dashboard" />
      <strong className="my-2">{t('SHARE_DASHBOARD_MODAL_GUESTS_TITLE')}</strong>
      <div className="d-flex flex-column w-100 gap-1 overflow-auto guests-list-share-dashboard">
        {loadingDelete ? (
          <div className="d-flex align-items-center justify-content-center w-100 rounded bg-light">
            <Spinner show={loadingDelete} />
          </div>
        ) : !guests.length ? (
          <div className="d-flex align-items-center justify-content-center px-2 py-1 rounded min-h-1/4 bg-light">
            <p className="my-2">{t('SHARE_DASHBOARD_MODAL_NO_GUESTS')}</p>
          </div>
        ) : (
          guests.map((item) => {
            const email = item?.guest?.email || item?.guestEmail;
            return (
              <div
                className="d-flex align-items-center justify-content-between px-2 py-1 rounded bg-light"
                key={email}
              >
                <div className="d-flex align-items-center justify-content-center text-xs">
                  <LetterLogo width={20} height={20} />
                  &ensp;
                  <span>{email}</span>
                  &ensp;
                  {handleGetInvitationItem(item)}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`access-level-tooltip-${email}`}>
                        {item?.accessType === DashboardAccessLevel.LIMITED_ACCESS
                          ? t('DASHBOARD_INVITATION_LIMITED_ACCESS_LEVEL')
                          : t('DASHBOARD_INVITATION_DETAILED_ACCESS_LEVEL')}
                      </Tooltip>
                    }
                  >
                    <span className="d-flex align-items-center pt-1 border-0">
                      {item?.accessType === DashboardAccessLevel.LIMITED_ACCESS ? (
                        <FaEyeSlash color={'#357b9eff'} width={16} height={16} />
                      ) : (
                        <FaEye color={'#357b9eff'} width={16} height={16} />
                      )}
                    </span>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`copy-link-tooltip-${email}`}>
                        {copySuccess[email]
                          ? t('COPIED_INVITATION_LINK') || 'Link copied!'
                          : t('COPY_INVITATION_LINK') || 'Copy invitation link'}
                      </Tooltip>
                    }
                  >
                    <button
                      onClick={() => handleCopyInvitationLink(item)}
                      className="bg-transparent border-0 cursor-pointer p-0"
                      type="button"
                      disabled={loadingResend[email]}
                      aria-label={t('COPY_INVITATION_LINK') || 'Copy invitation link'}
                    >
                      {copySuccess[email] ? (
                        <CopyCheck />
                      ) : (
                        <CopyLink width="16" height="16" fill="#357b9eff" />
                      )}
                    </button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`resend-tooltip-${email}`}>
                        {t('SHARE_DASHBOARD_RESEND_INVITATION') || 'Resend invitation'}
                      </Tooltip>
                    }
                  >
                    <button
                      onClick={() => handleResendInvitation(email)}
                      className="bg-transparent border-0 cursor-pointer p-0 d-flex align-items-center"
                      type="button"
                      disabled={loadingResend[email]}
                      aria-label={t('SHARE_DASHBOARD_RESEND_INVITATION') || 'Resend invitation'}
                    >
                      <SendOutlined style={{ fontSize: '16px', color: '#357b9eff' }} />
                    </button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`delete-tooltip-${email}`}>
                        {t('DELETE_TOOLTIP') || 'Delete'}
                      </Tooltip>
                    }
                  >
                    <button
                      onClick={() => handleDeleteGuest(email)}
                      className="bg-transparent border-0 cursor-pointer p-0"
                      type="button"
                      disabled={loadingResend[email]}
                      aria-label={t('DELETE_TOOLTIP') || 'Delete'}
                    >
                      <TrashIcon width={20} height={20} cursor="pointer" />
                    </button>
                  </OverlayTrigger>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ShareDashboard;
